import SelectCity from "@/pages/createDiscount/componets/SelectCity";
import SelectCountry from "@/pages/createDiscount/componets/SelectCountry";
import { DiscountCity, Province } from "@/types";
import { cloneDeep } from "lodash";
import { PlusIcon, XIcon } from "lucide-react";
import React, { useEffect, useState } from "react";

interface IProps {
  discountCities?: DiscountCity[]; // set this value for editing
}

interface SelectedCountryCity {
  countryCode: string;
  cityCodes: string[];
}

const DEFAULT_SELECT_COUNTRY_CITY: SelectedCountryCity = {
  countryCode: "",
  cityCodes: [],
};

export interface SelectCountryCityRef {
  getDiscountCities: () => DiscountCity[];
  getCountryCodes: () => string[];
}

const SelectCountryCity = React.forwardRef<SelectCountryCityRef, IProps>(
  ({ discountCities }, ref) => {
    const [selectedCountryCity, setSelectedCountryCity] = useState<
      SelectedCountryCity[]
    >([DEFAULT_SELECT_COUNTRY_CITY]);
    const [provinceData, setProvinceData] = useState<Province[]>([]);

    useEffect(() => {
      // only once, for editing
      if (
        discountCities &&
        discountCities.length > 0 &&
        JSON.stringify(selectedCountryCity) ===
          JSON.stringify([DEFAULT_SELECT_COUNTRY_CITY])
      ) {
        const allCountryCodes = Array.from(
          new Set(discountCities.map((city) => city.countryCode)),
        );
        setSelectedCountryCity(
          allCountryCodes.map((code) => ({
            countryCode: code,
            cityCodes: discountCities
              .filter((city) => city.countryCode === code)
              .map((city) => city.cityCode),
          })),
        );
      }
    }, [discountCities]);

    // expose the value
    React.useImperativeHandle(ref, () => ({
      getDiscountCities: () => {
        let discountCities: DiscountCity[] = [];
        selectedCountryCity.forEach((countryCity) => {
          const cities: DiscountCity[] = countryCity.cityCodes.map(
            (cityCode) => {
              const city = provinceData
                .flatMap((province: Province) => province.citys)
                .find((c) => c.cityCode === cityCode);
              const country = provinceData.find((province) =>
                province.citys.some((c) => c.cityCode === cityCode),
              );
              return {
                cityCode: city?.cityCode || "",
                cityName: city?.cityName || "",
                countryCode: country?.countryCode || "",
                countryName: country?.countryName || "",
              };
            },
          );
          discountCities = [...discountCities, ...cities];
        });
        return discountCities;
      },
      getCountryCodes: () => {
        let countryCodes: string[] = [];
        selectedCountryCity.forEach((countryCity) => {
          countryCodes = [...countryCodes, countryCity.countryCode];
        });
        return countryCodes;
      },
    }));

    const handleChangeCountry = (index: number, countryCode: string) => {
      const newSelectedCountryCity = cloneDeep(selectedCountryCity);
      newSelectedCountryCity[index].countryCode = countryCode;
      newSelectedCountryCity[index].cityCodes = [];
      setSelectedCountryCity(newSelectedCountryCity);
    };

    // toggle city code
    const handleChangeCity = (index: number, cityCode: string) => {
      const newSelectedCountryCity = cloneDeep(selectedCountryCity);
      const cityCodes = newSelectedCountryCity[index].cityCodes;
      const newCityCodes = cityCodes.includes(cityCode)
        ? cityCodes.filter((code) => code !== cityCode)
        : [...cityCodes, cityCode];
      newSelectedCountryCity[index].cityCodes = newCityCodes;
      setSelectedCountryCity(newSelectedCountryCity);
    };

    const handleAddCityProvince = (inputProvinceData: Province[]) => {
      // check if the province data is already in the provinceData by provinceCode and countryCode
      const newProvinceData = inputProvinceData.filter((province) => {
        const isExist = provinceData.some(
          (prevProvince) =>
            prevProvince.countryCode === province.countryCode &&
            prevProvince.provinceCode === province.provinceCode,
        );
        return !isExist;
      });
      setProvinceData((prevState) => [...prevState, ...newProvinceData]);
    };

    const handleAddCountryCity = () => {
      const newSelectedCountryCity = cloneDeep(selectedCountryCity);
      newSelectedCountryCity.push({
        countryCode: "",
        cityCodes: [],
      });
      setSelectedCountryCity(newSelectedCountryCity);
    };

    const handleRemoveCountryCity = (index: number) => {
      const newSelectedCountryCity = cloneDeep(selectedCountryCity);
      newSelectedCountryCity.splice(index, 1);
      setSelectedCountryCity(newSelectedCountryCity);
    };

    return (
      <>
        <div className="w-full space-y-3">
          {selectedCountryCity.map(
            (countryCity: SelectedCountryCity, index) => (
              <div
                key={index}
                className="flex w-full items-center gap-8 max-md:gap-5"
              >
                <div className="flex w-full items-start gap-8 max-sm:flex-col max-sm:gap-5">
                  <div className="flex-1 max-sm:w-full">
                    <div className="flex w-full flex-col">
                      <SelectCountry
                        value={countryCity.countryCode}
                        onChange={(countryCode) => {
                          handleChangeCountry(index, countryCode);
                        }}
                        excludedValues={selectedCountryCity
                          .filter(
                            (selected) =>
                              selected.countryCode !== countryCity.countryCode,
                          )
                          .map((countryCity) => countryCity.countryCode)}
                      />
                    </div>
                  </div>
                  <div className="flex-1 max-sm:w-full">
                    <div className=" flex w-full flex-col">
                      <SelectCity
                        countryCode={countryCity.countryCode}
                        values={countryCity.cityCodes}
                        onChange={(cityCode) => {
                          handleChangeCity(index, cityCode);
                        }}
                        onCityDataChange={(data) => {
                          if (data) {
                            handleAddCityProvince(data.provinces);
                          }
                        }}
                        disabled={!countryCity.countryCode}
                      />
                    </div>
                  </div>
                </div>
                {selectedCountryCity.length > 1 && (
                  <button
                    className="flex size-10 cursor-pointer items-center justify-center"
                    onClick={() => handleRemoveCountryCity(index)}
                  >
                    <XIcon className="size-5" />
                  </button>
                )}
              </div>
            ),
          )}
          <button
            className="flex size-10 cursor-pointer items-center justify-center"
            onClick={handleAddCountryCity}
          >
            <PlusIcon className="size-5" />
          </button>
        </div>
      </>
    );
  },
);

export default SelectCountryCity;
